import PageHeader from "@/components/common/PageHeader";
import { backgroundDbWorkRefetchIntervalMs } from "@/lib/background-db-work-pause";
import {
  monitoringService,
  type LivePresenceEntry,
} from "@/services/monitoring.service";
import { useQuery } from "@tanstack/react-query";
import {
  Globe,
  Laptop,
  Monitor,
  Radio,
  RefreshCw,
  Search,
  Smartphone,
  Users,
  Wifi,
  WifiOff,
} from "lucide-react";
import { useState } from "react";

const PLATFORM_CONFIG: Record<
  string,
  { label: string; icon: typeof Monitor; color: string }
> = {
  web: {
    label: "Web",
    icon: Globe,
    color: "bg-blue-100 text-blue-700",
  },
  desktop: {
    label: "Desktop",
    icon: Monitor,
    color: "bg-purple-100 text-purple-700",
  },
  mobile: {
    label: "Mobile",
    icon: Smartphone,
    color: "bg-green-100 text-green-700",
  },
};

function PlatformBadge({ platform }: { platform: string }) {
  const config = PLATFORM_CONFIG[platform] || {
    label: platform,
    icon: Laptop,
    color: "bg-slate-100 text-slate-700",
  };
  const Icon = config.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${config.color}`}
    >
      <Icon className="h-3 w-3" />
      {config.label}
    </span>
  );
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

export default function LivePresencePage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [platformFilter, setPlatformFilter] = useState<string>("all");

  const { data: stats, refetch: refetchStats } = useQuery({
    queryKey: ["presence-stats"],
    queryFn: () => monitoringService.getPresenceStats(),
    refetchInterval: () => backgroundDbWorkRefetchIntervalMs(15_000),
  });

  const {
    data: connections,
    isLoading,
    refetch: refetchConnections,
  } = useQuery({
    queryKey: ["live-presence"],
    queryFn: () => monitoringService.getLivePresence(),
    refetchInterval: () => backgroundDbWorkRefetchIntervalMs(10_000),
  });

  const handleRefresh = () => {
    refetchStats();
    refetchConnections();
  };

  const filteredConnections = (connections || []).filter((c) => {
    if (platformFilter !== "all" && c.platform !== platformFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        c.tenantId.toLowerCase().includes(q) ||
        c.userId.toLowerCase().includes(q) ||
        c.deviceId.toLowerCase().includes(q) ||
        (c.appState?.currentPage || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Live Presence"
        subtitle="Real-time view of all connected client devices"
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <StatCard
          icon={Users}
          label="Total Online"
          value={stats?.totalConnections ?? 0}
          color="bg-emerald-50 text-emerald-600"
        />
        <StatCard
          icon={Wifi}
          label="Tenants Online"
          value={stats?.tenantsOnline ?? 0}
          color="bg-blue-50 text-blue-600"
        />
        <StatCard
          icon={WifiOff}
          label="Tenants Offline"
          value={stats?.tenantsOffline ?? 0}
          color="bg-red-50 text-red-600"
        />
        <StatCard
          icon={Radio}
          label="Active Tenants"
          value={stats?.totalActiveTenants ?? 0}
          color="bg-slate-50 text-slate-600"
        />
      </div>

      {/* Platform Breakdown */}
      {stats?.byPlatform && Object.keys(stats.byPlatform).length > 0 && (
        <div className="flex flex-wrap gap-3">
          {Object.entries(stats.byPlatform).map(([platform, count]) => (
            <div
              key={platform}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2"
            >
              <PlatformBadge platform={platform} />
              <span className="text-sm font-bold text-slate-900">{count}</span>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search by tenant, user, or device ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <select
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          >
            <option value="all">All Platforms</option>
            <option value="web">Web</option>
            <option value="desktop">Desktop</option>
            <option value="mobile">Mobile</option>
          </select>

          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Connections Table */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
          </div>
        ) : filteredConnections.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <WifiOff className="mb-3 h-12 w-12" />
            <p className="text-sm font-medium">No active connections</p>
            <p className="text-xs">
              Clients will appear here when they send heartbeats
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50">
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Device
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Platform
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Current Page
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Sync Queue
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Version
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Last Seen
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredConnections.map((conn) => (
                <ConnectionRow key={conn.deviceId} connection={conn} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-center text-xs text-slate-400">
        Auto-refreshes every 10 seconds &bull; {filteredConnections.length}{" "}
        connection(s) shown
      </p>
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────

function StatCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: typeof Users;
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-3">
        <div className={`rounded-lg p-2 ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

function ConnectionRow({ connection }: { connection: LivePresenceEntry }) {
  const c = connection;
  return (
    <tr className="hover:bg-slate-50/50 transition-colors">
      <td className="px-4 py-3">
        <div>
          <p className="text-sm font-medium text-slate-900 truncate max-w-[180px]">
            {c.deviceId}
          </p>
          <p className="text-xs text-slate-400 truncate max-w-[180px]">
            {c.tenantId.slice(0, 8)}... &middot; {c.userId.slice(0, 8)}...
          </p>
        </div>
      </td>
      <td className="px-4 py-3">
        <PlatformBadge platform={c.platform} />
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-slate-600">
          {c.appState?.currentPage || "—"}
        </span>
        {c.appState?.activeModule && (
          <span className="ml-2 rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-600">
            {c.appState.activeModule}
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        {c.syncPendingChanges > 0 ? (
          <span
            className={`text-sm font-semibold ${
              c.syncPendingChanges > 50
                ? "text-red-600"
                : c.syncPendingChanges > 10
                  ? "text-amber-600"
                  : "text-slate-600"
            }`}
          >
            {c.syncPendingChanges}
          </span>
        ) : (
          <span className="text-sm text-slate-400">0</span>
        )}
      </td>
      <td className="px-4 py-3">
        <span className="text-xs text-slate-500">{c.appVersion || "—"}</span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-xs text-slate-500">
            {timeAgo(c.lastHeartbeat)}
          </span>
        </div>
      </td>
    </tr>
  );
}
